import { createHandler, createReducer } from '@app/util';
import { AddValdrConstraints, AddValdrValidators, ValdrActions, ValdrActionTypes } from '@app/valdr/actions/valdr';
import { RawPropertyConstraints, ValdrValidators, ValidatorConfig } from '@app/valdr/models';
import { ConstraintValidator, ValdrConstraints } from '@app/valdr/models/valdr-constraints';
import {
  createValidator,
  digitsValidator, emailValidator,
  maxLengthValidator,
  maxValidator,
  minLengthValidator,
  minValidator,
  patternValidator,
  requiredValidator,
  sizeValidator
} from '@app/valdr/validators';
import * as R from 'ramda';

export interface State {
  constraints: ValdrConstraints;
  validators: ValdrValidators;
}

export const initialState: State = {
  constraints: null,
  validators: {
    required: requiredValidator,
    maxLength: maxLengthValidator,
    minLength: minLengthValidator,
    max: maxValidator,
    min: minValidator,
    size: sizeValidator,
    pattern: patternValidator,
    digits: digitsValidator,
    email: emailValidator
  }
};

type GetConstraints = (s: State) => ValdrConstraints;
export const getConstraints: GetConstraints = R.prop('constraints');

const removeDtoSuffix = (name: string): string =>
  R.endsWith('Dto', name) ? R.dropLast(3, name) : name;

const mapValidators = (propConstraints: ConstraintValidator, propertyName: string, validators: ValdrValidators) =>
  R.mapObjIndexed(
    (validatorConfig: ValidatorConfig, validatorName: string) => createValidator({
      name: validatorName,
      propertyName: propertyName,
      config: validatorConfig,
      validate: validators[validatorName]
    }),
    propConstraints
  );

const mapProperties = (entityConstraints: RawPropertyConstraints, validators: ValdrValidators) =>
  R.mapObjIndexed(
    (propConstraints: ConstraintValidator<any>, propertyName: string) => mapValidators(propConstraints, propertyName, validators),
    entityConstraints
  );

const addConstraints = (state: State, {payload: newConstraints}: AddValdrConstraints): State => {
  const {validators, constraints} = state;

  const constraintsWithUpdatedNames = R.reduce((res, current: string) => ({
    ...res,
    [removeDtoSuffix(current)]: newConstraints[current]
  }), {}, R.keys(newConstraints));

  const constraintsWithValidators: ValdrConstraints = R.mapObjIndexed(
    (entityConstraints: RawPropertyConstraints) => mapProperties(entityConstraints, validators),
    constraintsWithUpdatedNames
  );

  return R.mergeRight(state, {
    constraints: R.mergeDeepRight<any, any>(constraints, constraintsWithValidators)
  });
};

const addValidators = (state: State, {payload: validators}: AddValdrValidators): State =>
  R.mergeRight(state, {
    validators: R.mergeDeepRight<any, any>(state.validators, validators)
  });

const reduce = createReducer<State, ValdrActions>([
  createHandler(ValdrActionTypes.AddValdrConstraints, addConstraints),
  createHandler(ValdrActionTypes.AddValdrValidators, addValidators)
],
initialState
);

export function valdrReducer(state, action) {
  return reduce(state, action);
}
